import { NativeSelect } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useLanguageAwareRouting } from '@/hooks/useLanguageAwareRouting';
import { storeLanguage } from '@/i18n/languageDetection';

export interface LanguageOption {
  value: string;
  label: string;
  /** The language's own name, for the Preferences select. */
  name: string;
  flag: string;
}

export const LANGUAGES: LanguageOption[] = [
  { value: 'en', label: 'EN', name: 'English', flag: '🇬🇧' },
  { value: 'de', label: 'DE', name: 'Deutsch', flag: '🇩🇪' },
  { value: 'fr', label: 'FR', name: 'Français', flag: '🇫🇷' },
  { value: 'es', label: 'ES', name: 'Español', flag: '🇪🇸' },
  { value: 'pt', label: 'PT', name: 'Português', flag: '🇵🇹' },
  { value: 'zh', label: '中文', name: '中文', flag: '🇨🇳' },
  { value: 'th', label: 'ไทย', name: 'ไทย', flag: '🇹🇭' },
  { value: 'ar', label: 'العربية', name: 'العربية', flag: '🇸🇦' },
];

/** The current language and a switch that stores the choice and moves the route to the new prefix. */
export function useLanguageSwitch() {
  const { i18n } = useTranslation();
  const location = useLocation();
  const { createLanguageURL } = useLanguageAwareRouting();

  const current = LANGUAGES.find((l) => i18n.language.startsWith(l.value))?.value ?? 'en';

  const switchTo = async (lang: string) => {
    await i18n.changeLanguage(lang);
    storeLanguage(lang);

    let currentPath = location.pathname.replace(/^\/[a-z]{2}(?=\/|$)/, '');
    if (!currentPath.startsWith('/')) {
      currentPath = `/${currentPath}`;
    }
    window.location.replace(`#${createLanguageURL(currentPath, lang)}${location.search}`);
  };

  return { current, switchTo };
}

export function LanguageSelector() {
  const { current, switchTo } = useLanguageSwitch();

  return (
    <NativeSelect
      value={current}
      onChange={(e) => void switchTo(e.target.value)}
      variant="unstyled"
    >
      {LANGUAGES.map((lang) => (
        <option key={lang.value} value={lang.value}>
          {lang.flag} {lang.label}
        </option>
      ))}
    </NativeSelect>
  );
}
